import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NotesList from '../../components/display/NotesList';
import NoteModal from '../../components/display/NoteModal';
import { fetchNotes, FilterOptions, notesService } from '../../services/notesService';
import { useAuth } from '../../contexts/AuthContext';
import { useNoteModal } from '../../hooks/useNoteModal';
import { PAGES } from '../../seo/documentHead';

const NotesListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  // Check if user is admin or moderator
  const isAdminOrModerator = currentUser?.role === 'admin' || currentUser?.role === 'moderator';

  // Parse query parameters from URL
  const queryParams = new URLSearchParams(location.search);

  // Modal state
  const { selectedNoteId, isModalOpen, openModal, closeModal } = useNoteModal({ basePath: '/notes' });
  const [modalNote, setModalNote] = useState<any>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [isHearted, setIsHearted] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  const [notes, setNotes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(parseInt(queryParams.get('page') || '1', 10));
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalNotes, setTotalNotes] = useState<number>(0);
  const [filters, setFilters] = useState<FilterOptions>({
    status: queryParams.get('status') || undefined,
    category: queryParams.get('category') || undefined,
    tag: queryParams.get('tag') || undefined,
    location: queryParams.get('location') || undefined,
    sortBy: queryParams.get('sortBy') || 'newest',
    search: queryParams.get('search') || undefined,
    limit: 10,
  });
  
  // Load initial notes when filters change (reset pagination)
  useEffect(() => {
    const loadInitialNotes = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const result = await fetchNotes({
          ...filters,
          page: 1, // Always start from page 1 when filters change
        });
        
        setNotes(result.notes);
        setTotalNotes(result.total);
        setTotalPages(Math.ceil(result.total / (filters.limit || 10)));
        setCurrentPage(1);
      } catch (err) {
        console.error('Error loading notes:', err);
        setError('Failed to load notes. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInitialNotes();
    
    // Update URL with current filters (but not page since we reset to 1)
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.category) params.set('category', filters.category);
    if (filters.tag) params.set('tag', filters.tag);
    if (filters.location) params.set('location', filters.location);
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.search) params.set('search', filters.search);
    
    const newUrl = `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState(null, '', newUrl);
    
  }, [filters, location.pathname]);
  
  // Handle filter changes
  const handleFilterChange = (newFilters: FilterOptions) => {
    setFilters(newFilters);
  };
  
  // Handle loading more notes (pagination)
  const handleLoadMore = async () => {
    if (currentPage >= totalPages || isLoadingMore) {
      return; // Don't load if we're already at the last page or currently loading
    }
    
    setIsLoadingMore(true);
    
    try {
      const nextPage = currentPage + 1;
      const result = await fetchNotes({
        ...filters,
        page: nextPage,
      });
      
      // Append new notes to existing ones
      setNotes(prevNotes => [...prevNotes, ...result.notes]);
      setCurrentPage(nextPage);
      
      // Update URL to reflect current page
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.category) params.set('category', filters.category);
      if (filters.tag) params.set('tag', filters.tag);
      if (filters.location) params.set('location', filters.location);
      if (filters.sortBy) params.set('sortBy', filters.sortBy);
      if (filters.search) params.set('search', filters.search);
      params.set('page', nextPage.toString());
      
      const newUrl = `${location.pathname}?${params.toString()}`;
      window.history.replaceState(null, '', newUrl);
      
    } catch (err) {
      console.error('Error loading more notes:', err);
      setError('Failed to load more notes. Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
  };
  
  // Handle note click - open modal instead of navigating
  const handleNoteClick = (noteId: string) => {
    openModal(noteId);
  };

  // Load note data when modal opens
  useEffect(() => {
    const loadModalNote = async () => {
      if (!selectedNoteId) {
        setModalNote(null);
        setModalError(null);
        return;
      }

      setModalLoading(true);
      setModalError(null);

      try {
        const noteData = await notesService.fetchPublicNoteContentByPublicId(selectedNoteId);
        setModalNote(noteData);

        // Check if current user is the owner
        const noteOwnerUserId = noteData?.contributorInfo?.userId;
        const currentUserId = currentUser?.id;
        setIsOwner(!!(noteOwnerUserId && currentUserId && noteOwnerUserId === currentUserId));

        // Default hearted to false (would be fetched from API for authenticated users)
        setIsHearted(false);
      } catch (err) {
        console.error('Error fetching note:', err);
        setModalError('Failed to load the note. Please try again later.');
      } finally {
        setModalLoading(false);
      }
    };

    loadModalNote();
  }, [selectedNoteId, currentUser?.id]);

  // Modal action handlers
  const handleHeartClick = useCallback(async () => {
    if (!modalNote || !(modalNote as any)._id) return;

    try {
      await notesService.heartNote((modalNote as any)._id);
      setIsHearted((prev: boolean) => !prev);
      setModalNote((prev: any) => ({
        ...prev,
        heart_count: isHearted ? prev.heart_count - 1 : prev.heart_count + 1
      }));
    } catch (err) {
      console.error('Error hearting note:', err);
    }
  }, [modalNote, isHearted]);

  const handleFlagClick = useCallback(async () => {
    if (!modalNote || !(modalNote as any)._id) return;
    try {
      await notesService.flagNote((modalNote as any)._id, 'Inappropriate content');
      alert('Note flagged for review.');
    } catch (err) {
      console.error('Error flagging note:', err);
      alert('Failed to flag note.');
    }
  }, [modalNote]);

  const handleShareClick = useCallback(() => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => alert('Link copied to clipboard!'))
      .catch(err => console.error('Failed to copy link:', err));
  }, []);

  const handleEditClick = useCallback(() => {
    if (!modalNote || !(modalNote as any)._id) return;
    navigate(`/my-notes/edit/${(modalNote as any)._id}`);
  }, [modalNote, navigate]);
  
  // Calculate if there are more pages available
  const hasMorePages = currentPage < totalPages;
  
  return (
    <div className="container py-4">
      <div className="row mb-4">
        <div className="col">
          <h1 className="display-5">{PAGES['/notes'].h1}</h1>
          <p className="lead">{PAGES['/notes'].intro}</p>
          {/* Show pagination info - disabled for now */}
          {totalNotes > 0 && (
            <p className="text-muted" style={{display:"none"}}>
              Showing {notes.length} of {totalNotes} notes
            </p>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <NotesList
            initialNotes={notes}
            isLoading={isLoading}
            error={error || undefined}
            showFilters={true}
            showStatusFilter={isAdminOrModerator}
            showRawStatus={isAdminOrModerator}
            onFilterChange={handleFilterChange}
            onNoteClick={handleNoteClick}
            onLoadMore={handleLoadMore}
            hasMorePages={hasMorePages}
            isLoadingMore={isLoadingMore}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        </div>
      </div>

      {/* Note Modal */}
      <NoteModal
        isOpen={isModalOpen}
        onClose={closeModal}
        note={modalNote}
        isLoading={modalLoading}
        error={modalError}
        isOwner={isOwner}
        isAdmin={isAdminOrModerator}
        isHearted={isHearted}
        onHeartClick={handleHeartClick}
        onFlagClick={handleFlagClick}
        onShareClick={handleShareClick}
        onEditClick={handleEditClick}
      />
    </div>
  );
};

export default NotesListPage; 